import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import SafeImage from "./SafeImage";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, subtotal, count, clear } = useCart();
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[rgba(9,18,13,0.7)] backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="relative w-full max-w-md bg-[color:var(--forest-2)] border-l border-brass-soft flex flex-col shadow-2xl" data-testid="cart-drawer">
        <div className="flex items-center justify-between p-5 border-b border-brass-soft">
          <h2 className="font-display text-xl text-gold">Your Curiosities <span className="text-parchment/60 text-sm">({count})</span></h2>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="text-parchment/80 hover:text-gold"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16 text-parchment/60">
              <p className="font-display text-lg mb-2">The cabinet is empty</p>
              <p className="text-sm">Wander the shop to gather your finds.</p>
            </div>
          )}
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-4 border-b border-brass-soft pb-4" data-testid={`cart-item-${i.product_id}`}>
              <SafeImage src={i.image} alt={i.title} className="w-20 h-24 object-cover corner-frame" />
              <div className="flex-1">
                <div className="font-display text-base text-cream leading-snug">{i.title}</div>
                <div className="font-serif-italic text-gold text-sm mt-1">${i.price.toFixed(2)}</div>
                <div className="flex items-center gap-3 mt-3">
                  {i.inventory_type !== "one_of_one" ? (
                    <div className="flex items-center border border-brass/40">
                      <button data-testid={`qty-dec-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity - 1)} className="p-1.5 text-parchment/80 hover:text-gold"><Minus size={14} /></button>
                      <span className="px-3 text-sm text-cream">{i.quantity}</span>
                      <button data-testid={`qty-inc-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 1)} className="p-1.5 text-parchment/80 hover:text-gold"><Plus size={14} /></button>
                    </div>
                  ) : (
                    <span className="text-[10px] font-caption text-gold border border-gold/50 px-2 py-0.5">Only One</span>
                  )}
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-parchment/60 hover:text-oxblood ml-auto"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="p-5 border-t border-brass-soft space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="font-caption text-xs text-parchment/70">Subtotal</span>
              <span className="font-display text-2xl text-gold">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-[11px] text-parchment/50 italic">Shipping calculated at checkout</p>
            <button data-testid="cart-checkout-btn" onClick={checkout} disabled={loading}
              className="btn-gold w-full">{loading ? "Preparing..." : "Proceed to Checkout"}</button>
            <button data-testid="cart-clear-btn" onClick={clear} className="w-full text-parchment/60 text-xs font-caption hover:text-oxblood py-1">Empty Cart</button>
          </div>
        )}
      </aside>
    </div>
  );
}
